import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import Button from '@mui/material/Button';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Croppie from 'croppie';
import { jsPDF } from 'jspdf';
import 'croppie/croppie.css';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';

function App() {
  const [imageSrc, setImageSrc] = useState(null);
  const [crestSrc, setCrestSrc] = useState(null);
  const [crestDimensions, setCrestDimensions] = useState(null);
  const [crestCorner, setCrestCorner] = useState('bottom-right');
  const [generatePdfDisabled, setGeneratePdfDisabled] = useState(true);
  const [optionalText, setOptionalText] = useState('');
  const [numberText, setNumberText] = useState('');
  const [originalFilename, setOriginalFilename] = useState('ansichtskarten');
  const [isDragging, setIsDragging] = useState(false);
  const croppieViewportRef = useRef(null);
  const croppieInstance = useRef(null);
  const fileInputRef = useRef(null);
  const textFieldRef = useRef(null);

  useEffect(() => {
    if (croppieViewportRef.current && !croppieInstance.current) {
      try {
        croppieInstance.current = new Croppie(croppieViewportRef.current, {
          viewport: { width: 600, height: 422 }, // A6 aspect ratio
          boundary: { width: '100%', height: 500 },
          showZoomer: true,
          enableOrientation: true
        });
        console.log('Croppie initialized successfully');
      } catch (error) {
        console.error('Failed to initialize Croppie:', error);
      }
    }

    return () => {
      if (croppieInstance.current) {
        try {
          croppieInstance.current.destroy();
        } catch (error) {
          console.error('Error destroying Croppie:', error);
        }
        croppieInstance.current = null;
      }
    };
  }, [imageSrc]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Enter' && !generatePdfDisabled) {
        handleGeneratePdf();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [generatePdfDisabled]);

  const handleImageUpload = (file) => {
    if (file) {
      setOriginalFilename(file.name.replace(/\.[^/.]+$/, ""));
      const reader = new FileReader();
      reader.onload = (event) => {
        setImageSrc(event.target.result);
        
        // Warte einen Moment, bis Croppie initialisiert ist
        setTimeout(() => {
          if (croppieInstance.current) {
            croppieInstance.current.bind({
              url: event.target.result
            }).then(() => {
              croppieInstance.current.setZoom(0);
              setGeneratePdfDisabled(false);
              if (fileInputRef.current) {
                fileInputRef.current.blur();
              }
              if (textFieldRef.current) {
                textFieldRef.current.focus();
              }
            }).catch((error) => {
              console.error('Croppie bind error:', error);
              setGeneratePdfDisabled(false);
            });
          } else {
            console.warn('Croppie instance not ready');
            setGeneratePdfDisabled(false);
          }
        }, 100);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCrestUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const imageUrl = event.target.result;
        setCrestSrc(imageUrl);
        const img = new Image();
        img.onload = () => {
          setCrestDimensions({ width: img.width, height: img.height });
        };
        img.src = imageUrl;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCrestCornerChange = (event) => {
    setCrestCorner(event.target.value);
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleImageUpload(files[0]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleImageUpload(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleGeneratePdf = () => {
    setGeneratePdfDisabled(true);

    if (croppieInstance.current && imageSrc) {
      croppieInstance.current.result({
        type: 'canvas',
        size: { width: 1748, height: 1240 }, // A6 at 300 DPI
        quality: 0.95,
        format: 'jpeg'
      }).then((croppedImageDataUrl) => {
        generatePdf(croppedImageDataUrl);
      }).catch((error) => {
        console.error('Croppie result error:', error);
        generatePdf(imageSrc);
      });
    } else if (imageSrc) {
      generatePdf(imageSrc);
    } else {
      console.error('No image available');
      setGeneratePdfDisabled(false);
    }
  };

  const generatePdf = (imageDataUrl) => {
    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = 297;
      const pageHeight = 210;
      const outerMargin = 5;
      const gap = 10;

      const cardWidth = ( (pageWidth - 2 * outerMargin) - gap ) / 2;
      const cardHeight = ( (pageHeight - 2 * outerMargin) - gap ) / 2;

      const positions = [
        { x: outerMargin, y: outerMargin },
        { x: outerMargin + cardWidth + gap, y: outerMargin },
        { x: outerMargin, y: outerMargin + cardHeight + gap },
        { x: outerMargin + cardWidth + gap, y: outerMargin + cardHeight + gap }
      ];

      positions.forEach(pos => {
        doc.addImage(imageDataUrl, 'JPEG', pos.x, pos.y, cardWidth, cardHeight);
        if (crestSrc && crestDimensions) {
          const maxDim = 20; // The crest will fit in a 20mm x 20mm box
          const aspectRatio = crestDimensions.width / crestDimensions.height;

          let crestWidth, crestHeight;
          if (crestDimensions.width > crestDimensions.height) {
            crestWidth = maxDim;
            crestHeight = maxDim / aspectRatio;
          } else {
            crestHeight = maxDim;
            crestWidth = maxDim * aspectRatio;
          }

          const crestMargin = 5; // 5mm from the edge
          let crestX, crestY;

          switch (crestCorner) {
            case 'top-left':
              crestX = pos.x + crestMargin;
              crestY = pos.y + crestMargin;
              break;
            case 'top-right':
              crestX = pos.x + cardWidth - crestWidth - crestMargin;
              crestY = pos.y + crestMargin;
              break;
            case 'bottom-left':
              crestX = pos.x + crestMargin;
              crestY = pos.y + cardHeight - crestHeight - crestMargin;
              break;
            case 'bottom-right':
            default:
              crestX = pos.x + cardWidth - crestWidth - crestMargin;
              crestY = pos.y + cardHeight - crestHeight - crestMargin;
              break;
          }

          doc.addImage(crestSrc, 'auto', crestX, crestY, crestWidth, crestHeight);
        }
      });

      doc.addPage();
      const postcardBackImage = document.getElementById('postcardBackImage');
      if (postcardBackImage) {
        positions.forEach(pos => {
          doc.addImage(postcardBackImage, 'PNG', pos.x, pos.y, cardWidth, cardHeight);

          if (optionalText) {
            doc.setFontSize(8);
            doc.setTextColor(0, 0, 0);
            doc.text(optionalText, pos.x + 5, pos.y + 10);
          }

          doc.setFontSize(8);
          doc.setTextColor(150, 150, 150);
          doc.text("Peter und Julian\nMayrhofer, Linz", pos.x + 5, pos.y + cardHeight - 9);

          if (numberText) {
            doc.setFontSize(8);
            doc.setTextColor(150, 150, 150);
            const textWidth = doc.getTextWidth(numberText);
            doc.text(numberText, pos.x + cardWidth - textWidth - 5, pos.y + cardHeight - 5.7);
          }
        });
      }

      doc.save(`${originalFilename}.pdf`);
    } catch (error) {
      console.error('PDF generation error:', error);
    }

    setGeneratePdfDisabled(false);
  };

  const handleOptionalTextChange = (e) => {
    setOptionalText(e.target.value);
  };

  const handleNumberTextChange = (e) => {
    setNumberText(e.target.value);
  };

  const theme = createTheme({
    typography: {
      fontFamily: "'Google Sans', 'Product Sans', Roboto, Arial, sans-serif",
    },
    palette: {
      primary: {
        main: '#4285f4',
      },
      secondary: {
        main: '#34a853',
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <div className="google-container">
        <div className="header-section">
          <h1 className="google-title">
            
            Ansichtskarten-Generator
          </h1>
          <p className="google-subtitle">
            Verwandle deine Fotos in druckfertige Ansichtskarten
          </p>
          
          
        </div>

        <div className="google-card">
          <div className="upload-section">
            <div 
              className={`upload-area ${isDragging ? 'dragging' : ''} ${imageSrc ? 'uploaded' : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <input 
                type="file" 
                id="imageUpload" 
                accept="image/*" 
                onChange={handleFileSelect} 
                style={{ display: 'none' }} 
                ref={fileInputRef} 
              />
              
              {!imageSrc ? (
                <div className="upload-content">
                  <div className="upload-icon">🖼️</div>
                  <h3 className="upload-title">Bild hier hinziehen oder hochladen</h3>
                  
                  <Button 
                    variant="contained"
                    onClick={() => fileInputRef.current.click()}
                    className="google-upload-button"
                    sx={{
                      background: 'linear-gradient(135deg, #4285f4, #1976d2)',
                      borderRadius: '24px',
                      textTransform: 'none',
                      fontSize: '16px',
                      fontWeight: 500,
                      padding: '12px 24px',
                      boxShadow: '0 2px 10px rgba(66, 133, 244, 0.3)',
                      marginTop: '30px',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 20px rgba(66, 133, 244, 0.4)',
                      }
                    }}
                  >
                    📁 Bild auswählen
                  </Button>
                </div>
              ) : (
                <div className="upload-success">
                  <div className="success-icon">✅</div>
                  <p className="success-text">Bild erfolgreich geladen!</p>
                  <button 
                    onClick={() => fileInputRef.current.click()}
                    className="change-image-button"
                  >
                    Anderes Bild wählen
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="text-input-section">
            <TextField
              label="💬 Text für die Rückseite (optional)"
              value={optionalText}
              onChange={handleOptionalTextChange}
              variant="outlined"
              fullWidth
              placeholder="Dein Text hier..."
              inputRef={textFieldRef}
              autoComplete="off"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  fontSize: '16px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4285f4',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4285f4',
                    borderWidth: '2px',
                  }
                },
                '& .MuiInputLabel-root': {
                  fontSize: '16px',
                  '&.Mui-focused': {
                    color: '#4285f4',
                  }
                }
              }}
            />
          </div>

          <div className="text-input-section">
            <TextField
              label="🔢 Nummer für die Rückseite (optional)"
              value={numberText}
              onChange={handleNumberTextChange}
              variant="outlined"
              fullWidth
              placeholder="Deine Nummer hier..."
              autoComplete="off"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  fontSize: '16px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4285f4',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4285f4',
                    borderWidth: '2px',
                  }
                },
                '& .MuiInputLabel-root': {
                  fontSize: '16px',
                  '&.Mui-focused': {
                    color: '#4285f4',
                  }
                }
              }}
            />
          </div>

          <div className="text-input-section">
            <div style={{ display: 'flex', gap: '16px', alignItems: 'stretch' }}>
              <Button variant="outlined" component="label" fullWidth sx={{ 
                borderRadius: '16px', 
                padding: '16.5px 14px', 
                textTransform: 'none', 
                fontSize: '16px',
                borderColor: 'rgba(0, 0, 0, 0.23)',
                color: 'rgba(0, 0, 0, 0.6)',
                fontFamily: "'Google Sans', 'Product Sans', Roboto, Arial, sans-serif",
                fontWeight: 400,
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  borderColor: '#4285f4',
                  backgroundColor: 'rgba(66, 133, 244, 0.04)'
                }
              }}>
                🛡️ Wappen hochladen (optional)
                <input type="file" hidden accept="image/*" onChange={handleCrestUpload} />
              </Button>
              <FormControl fullWidth>
                <InputLabel 
                  id="crest-corner-label"
                  sx={{
                    fontSize: '16px',
                    fontFamily: "'Google Sans', 'Product Sans', Roboto, Arial, sans-serif",
                    '&.Mui-focused': {
                      color: '#4285f4',
                    }
                  }}
                >
                  Position
                </InputLabel>
                <Select
                  labelId="crest-corner-label"
                  id="crest-corner-select"
                  value={crestCorner}
                  label="Position"
                  onChange={handleCrestCornerChange}
                  sx={{ 
                    borderRadius: '16px',
                    height: '56px',
                    '& .MuiSelect-select': {
                      fontSize: '16px',
                      fontFamily: "'Google Sans', 'Product Sans', Roboto, Arial, sans-serif",
                      fontWeight: 400,
                      color: 'rgba(0, 0, 0, 0.87)',
                      padding: '16.5px 14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4285f4',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4285f4',
                      borderWidth: '2px',
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: '16px',
                        fontFamily: "'Google Sans', 'Product Sans', Roboto, Arial, sans-serif",
                      },
                    },
                  }}
                >
                  <MenuItem value="top-left" sx={{ 
                    fontFamily: "'Google Sans', 'Product Sans', Roboto, Arial, sans-serif",
                    fontSize: '16px',
                    fontWeight: 400,
                    color: 'rgba(0, 0, 0, 0.87)'
                  }}>Oben Links</MenuItem>
                  <MenuItem value="top-right" sx={{ 
                    fontFamily: "'Google Sans', 'Product Sans', Roboto, Arial, sans-serif",
                    fontSize: '16px',
                    fontWeight: 400,
                    color: 'rgba(0, 0, 0, 0.87)'
                  }}>Oben Rechts</MenuItem>
                  <MenuItem value="bottom-left" sx={{ 
                    fontFamily: "'Google Sans', 'Product Sans', Roboto, Arial, sans-serif",
                    fontSize: '16px',
                    fontWeight: 400,
                    color: 'rgba(0, 0, 0, 0.87)'
                  }}>Unten Links</MenuItem>
                  <MenuItem value="bottom-right" sx={{ 
                    fontFamily: "'Google Sans', 'Product Sans', Roboto, Arial, sans-serif",
                    fontSize: '16px',
                    fontWeight: 400,
                    color: 'rgba(0, 0, 0, 0.87)'
                  }}>Unten Rechts</MenuItem>
                </Select>
              </FormControl>
            </div>
            {crestSrc && <p style={{ textAlign: 'center', marginTop: '10px', color: '#34a853' }}>Wappen erfolgreich geladen!</p>}
          </div>

          {imageSrc && (
            <div className="crop-section">
              <h3 className="crop-title">🎨 Bildbereich anpassen</h3>
              <div 
                id="croppie-viewport" 
                ref={croppieViewportRef}
                className="crop-viewport"
              ></div>
            </div>
          )}

          <div className="action-buttons">
            <Button 
              variant="contained"
              onClick={handleGeneratePdf}
              disabled={generatePdfDisabled}
              className="google-pdf-button"
              sx={{
                background: generatePdfDisabled 
                  ? '#e0e0e0' 
                  : 'linear-gradient(135deg, #34a853, #2e7d32)',
                color: generatePdfDisabled ? '#9e9e9e' : 'white',
                borderRadius: '24px',
                textTransform: 'none',
                fontSize: '18px',
                fontWeight: 600,
                padding: '16px 32px',
                minWidth: '200px',
                boxShadow: generatePdfDisabled 
                  ? 'none' 
                  : '0 4px 15px rgba(52, 168, 83, 0.3)',
                '&:hover': {
                  transform: generatePdfDisabled ? 'none' : 'translateY(-2px)',
                  boxShadow: generatePdfDisabled 
                    ? 'none' 
                    : '0 6px 25px rgba(52, 168, 83, 0.4)',
                },
                '&:disabled': {
                  background: '#e0e0e0',
                  color: '#9e9e9e',
                }
              }}
            >
              {generatePdfDisabled ? '⏳ Laden...' : '📄 PDF erstellen'}
            </Button>
          </div>
        </div>

        <img id="postcardBackImage" src={`${process.env.PUBLIC_URL}/ansichtskarte-hinten.png`} alt="Kartenrückseite" style={{ display: 'none' }} />
      </div>
    </ThemeProvider>
  );
}

export default App;

